import React, { useEffect, useState, useCallback } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, today, formatApiError, hasWrite, isApprover, isAccountant, isAdmin } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BankSelect, useBankAccounts } from "../components/BankAccounts";
import { askReason } from "../components/ApprovalLock";
import { BranchTransferDialog, PendingTransfers } from "../components/BranchTools";
import { Autocomplete } from "../components/Autocomplete";
import { CashbookLockReport } from "../components/CashbookLockReport";
import { AttachmentsDialog, AttachButton, AttachPicker, uploadAttachments } from "../components/Attachments";
import { ClosingCountDialog, ClosingCountSummary } from "../components/ClosingCount";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { FilePlus2, Plus, Receipt, Trash2, Wallet, Landmark, Lock, LockOpen, FileWarning } from "lucide-react";
import { toast } from "sonner";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";

function ModeBadge({ mode }) {
  const map = {
    cash: "bg-accent/15 text-accent",
    bank: "bg-primary/10 text-primary",
    credit: "bg-destructive/10 text-destructive",
    upi: "bg-[hsl(var(--positive))]/15 text-[hsl(var(--positive))]",
    qr: "bg-destructive/10 text-destructive",
  };
  return <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded num font-bold ${map[mode] || map.bank}`}>{mode}</span>;
}

/* ---------------- Invoice dialog ---------------- */
function InvoiceDialog({ onDone, date }) {
  const [open, setOpen] = useState(false);
  const [nextInfo, setNextInfo] = useState(null);
  const [form, setForm] = useState({ amount: "", party_name: "", account_name: "", vehicle_number: "", description: "", invoice_type: "Consolidated", source: "tata_motors", invoice_number: "", challan_number: "", gst: true, gst_percent: "18", settlement: "cash", starting_number: "", paid_amount: "", paid_mode: "cash", paid_cash: "", paid_online: "", online_method: "bank", online_reference: "" });

  useEffect(() => {
    if (open) api.get("/invoices/next-number").then((r) => { setNextInfo(r.data); if (r.data.challan_next) setForm((f) => ({ ...f, challan_number: f.challan_number || String(r.data.challan_next) })); });
  }, [open]);

  const save = async () => {
    try {
      const amt = parseFloat(form.amount);
      const noParty = date >= "2026-10-28";
      const isCredit = form.settlement === "credit" || form.settlement === "partial";
      if (!noParty && (!form.party_name || !form.party_name.trim())) return toast.error("Party required");
      if (noParty && !(form.account_name || "").trim()) return toast.error("Account name is required");
      if (noParty && isCredit && (form.driver_mobile || "").replace(/\D/g, "").length < 10) return toast.error("Driver mobile number (10 digits) is required for credit invoices");
      if (form.settlement === "credit" && !(form.account_name || "").trim()) return toast.error("Account name is mandatory for credit invoices");
      if (isNaN(amt) || amt < 0) return toast.error("Enter a valid amount (0 or more)");
      if (form.source === "outside" && !form.invoice_number.trim()) return toast.error("Enter the outside invoice number");
      if (form.source === "tata_motors" && !(form.job_card_number || "").trim()) return toast.error("Job card / order number is required for Tata Motors invoices");
      if (date >= "2026-10-20" && (form.invoice_type || "").toUpperCase() === "DEF" && form.source === "tata_motors" && !(form.challan_number || "").trim()) return toast.error("Challan number is required for DEF invoices");
      const payload = {
        date, amount: Math.round(amt * 100) / 100, party_name: noParty ? "" : form.party_name, account_name: form.account_name, driver_mobile: form.driver_mobile || "",
        vehicle_number: form.vehicle_number, description: form.description,
        invoice_type: form.invoice_type, settlement: form.settlement,
        source: form.source, invoice_number: form.invoice_number || null,
        challan_number: form.challan_number,
        job_card_number: form.job_card_number || "",
        gst: form.source === "outside" ? form.gst : true,
        gst_percent: form.source === "outside" && form.gst ? (parseFloat(form.gst_percent) || 0) : 0,
      };
      if (form.settlement === "partial") {
        payload.paid_cash = parseFloat(form.paid_cash) || 0;
        payload.paid_online = parseFloat(form.paid_online) || 0;
        payload.online_method = form.online_method || "bank";
        payload.online_reference = form.online_reference || "";
        if ((payload.paid_online || 0) > 0 && !payload.online_reference.trim())
          return toast.error("Add a reference number for the online portion");
      }
      if (nextInfo && !nextInfo.initialized && form.starting_number)
        payload.starting_number = parseInt(form.starting_number);
      const { data } = await api.post("/invoices", payload);
      toast.success(`Invoice ${data.invoice_no} created${data.receipt_no ? ` · receipt ${data.receipt_no}` : ""}`);
      setOpen(false);
      setForm({ amount: "", party_name: "", account_name: "", driver_mobile: "", vehicle_number: "", description: "", invoice_type: "Consolidated", source: "tata_motors", invoice_number: "", challan_number: "", gst: true, gst_percent: "18", settlement: "cash", starting_number: "", paid_amount: "", paid_mode: "cash", paid_cash: "", paid_online: "", online_method: "bank", online_reference: "" });
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-invoice-btn" className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors">
          <FilePlus2 size={16} /> New Invoice
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center justify-between">
            <span>New Invoice</span>
            {nextInfo && (
              <span className="num text-sm px-2 py-1 rounded bg-accent/15 text-accent">{nextInfo.preview}</span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {nextInfo && !nextInfo.initialized && (
            <div className="rounded-md bg-muted p-3">
              <label className={label}>Starting invoice number (set once)</label>
              <input data-testid="invoice-start-input" type="number" className={field} placeholder="e.g. 1"
                value={form.starting_number} onChange={(e) => setForm({ ...form, starting_number: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1">After the first invoice, numbers auto-increment.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {date < "2026-10-28" ? (
            <div>
              <label className={label}>Party name</label>
              <Autocomplete field="party" testid="invoice-party" value={form.party_name}
                onChange={(v) => setForm({ ...form, party_name: v })} placeholder="Start typing…" />
            </div>
            ) : (
            <div>
              <label className={label}>Driver mobile {["credit", "partial"].includes(form.settlement) ? <span className="text-destructive">*</span> : <span className="text-muted-foreground font-normal text-xs">(optional)</span>}</label>
              <input data-testid="invoice-driver-mobile" inputMode="numeric" className={field + " num"} value={form.driver_mobile || ""}
                onChange={(e) => setForm({ ...form, driver_mobile: e.target.value.replace(/\D/g, "").slice(-10) })} placeholder="10-digit mobile" />
            </div>
            )}
            <div className={date < "2026-10-28" ? "" : "order-first"}>
              <label className={label}>Account name {(form.settlement === "credit" || date >= "2026-10-28") ? <span className="text-destructive">*</span> : <span className="text-muted-foreground font-normal text-xs">(optional)</span>}</label>
              <Autocomplete field="account" testid="invoice-account" value={form.account_name}
                onChange={(v) => setForm({ ...form, account_name: v })} placeholder={["credit", "partial"].includes(form.settlement) ? "Pick a credit account" : date >= "2026-10-28" ? "Customer / account name" : "Group / account (optional)"} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Vehicle number</label>
              <Autocomplete field="vehicle" testid="invoice-vehicle" value={form.vehicle_number}
                onChange={(v) => setForm({ ...form, vehicle_number: v })} placeholder="DL01AB1234" />
            </div>
            <div>
              <label className={label}>{form.source === "outside" && form.gst ? "Amount (before GST)" : "Amount (₹)"}</label>
              <input data-testid="invoice-amount" type="number" step="0.01" min="0" className={field} value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
            </div>
          </div>
          <div>
            <label className={label}>Invoice type</label>
            <div className="grid grid-cols-4 gap-2">
              {["Consolidated", "Spare Parts", "DEF", "Other"].map((t) => (
                <button key={t} type="button" data-testid={`invoice-type-${t.replace(/\s/g, "-").toLowerCase()}`}
                  onClick={() => setForm({ ...form, invoice_type: t })}
                  className={`h-10 rounded-md text-xs border transition-colors ${form.invoice_type === t ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={label}>Invoice source</label>
            <div className="grid grid-cols-2 gap-2">
              {[{ v: "tata_motors", l: "Tata Motors" }, { v: "outside", l: "Outside" }].map((s) => (
                <button key={s.v} type="button" data-testid={`invoice-source-${s.v}`} onClick={() => setForm({ ...form, source: s.v })}
                  className={`h-10 rounded-md text-sm border transition-colors ${form.source === s.v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{s.l}</button>
              ))}
            </div>
          </div>
          {form.source === "outside" ? (
            <>
              <div>
                <label className={label}>Outside invoice number</label>
                <input data-testid="invoice-number" className={field} value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="Vendor bill no." />
              </div>
              <div>
                <label className={label}>GST</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" data-testid="invoice-gst-yes" onClick={() => setForm({ ...form, gst: true })}
                    className={`h-10 rounded-md text-sm border transition-colors ${form.gst ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>GST</button>
                  <button type="button" data-testid="invoice-gst-no" onClick={() => setForm({ ...form, gst: false })}
                    className={`h-10 rounded-md text-sm border transition-colors ${!form.gst ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>Non-GST</button>
                </div>
                {form.gst && (
                  <div className="mt-2 flex items-center gap-2">
                    <input data-testid="invoice-gst-percent" type="number" step="0.01" min="0" className={field + " w-28"} value={form.gst_percent}
                      onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} placeholder="GST %" />
                    <span className="text-xs text-muted-foreground">
                      GST ₹{((((parseFloat(form.amount) || 0) * (parseFloat(form.gst_percent) || 0)) / 100)).toFixed(2)} · Total ₹{(((parseFloat(form.amount) || 0) * (1 + (parseFloat(form.gst_percent) || 0) / 100))).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div>
              <label className={label}>Tata invoice number <span className="text-muted-foreground font-normal">(optional — blank = auto)</span></label>
              <input data-testid="invoice-number" className={field} value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="Leave blank to auto-number" />
            </div>
          )}
          {form.source === "tata_motors" && (
            <div>
              <label className={label}>Job card / order number <span className="text-destructive">*</span></label>
              <input data-testid="invoice-jobcard" className={field + " num"} value={form.job_card_number || ""}
                onChange={(e) => setForm({ ...form, job_card_number: e.target.value.toUpperCase() })} placeholder={nextInfo?.job_card_example || "Job card no."} />
            </div>
          )}
          {form.source === "tata_motors" && (
            <div>
              <label className={label}>Challan number <span className="text-destructive font-normal">(required for DEF invoices from 20-10-2026)</span></label>
              <input data-testid="invoice-challan" className={field} value={form.challan_number}
                onChange={(e) => setForm({ ...form, challan_number: e.target.value })} placeholder="Delivery challan no." />
            </div>
          )}
          <div>
            <label className={label}>Description</label>
            <input data-testid="invoice-desc" className={field} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Goods / service" />
          </div>
          <div>
            <label className={label}>Payment received as</label>
            <div className="grid grid-cols-4 gap-2">
              {["cash", "bank", "credit", "partial"].map((s) => (
                <button key={s} type="button" data-testid={`invoice-settle-${s}`}
                  onClick={() => setForm({ ...form, settlement: s })}
                  className={`h-10 rounded-md text-xs capitalize border transition-colors ${form.settlement === s ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>
                  {s}
                </button>
              ))}
            </div>
            {form.settlement === "credit" && (
              <p className="text-xs text-destructive mt-1.5">Unpaid — added to the account's credit ledger.</p>
            )}
            {form.settlement === "partial" && (
              <div className="mt-2 space-y-2 rounded-md bg-muted p-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium mb-1 block">Cash received (₹)</label>
                    <input data-testid="invoice-paid-cash" type="number" step="0.01" min="0" className={field} placeholder="0.00"
                      value={form.paid_cash} onChange={(e) => setForm({ ...form, paid_cash: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Online received (₹)</label>
                    <input data-testid="invoice-paid-online" type="number" step="0.01" min="0" className={field} placeholder="0.00"
                      value={form.paid_online} onChange={(e) => setForm({ ...form, paid_online: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-[1fr_1.4fr] gap-2">
                  <div className="grid grid-cols-3 gap-1">
                    {["bank", "upi", "qr"].map((m) => (
                      <button key={m} type="button" data-testid={`invoice-online-method-${m}`} onClick={() => setForm({ ...form, online_method: m })}
                        className={`h-10 rounded-md text-xs uppercase border transition-colors ${form.online_method === m ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{m}</button>
                    ))}
                  </div>
                  <input data-testid="invoice-online-reference" className={field} placeholder="Online reference / UTR no."
                    value={form.online_reference} onChange={(e) => setForm({ ...form, online_reference: e.target.value })} />
                </div>
                <p className="text-xs text-muted-foreground">
                  Balance ₹{Math.max((parseFloat(form.amount) || 0) - (parseFloat(form.paid_cash) || 0) - (parseFloat(form.paid_online) || 0), 0).toFixed(2)} goes to credit ledger.
                </p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <button data-testid="invoice-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Create invoice</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Receipt dialog ---------------- */
function ReceiptDialog({ onDone, date }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", party_name: "", vehicle_number: "", description: "", mode: "cash", is_credit_recovery: false });
  const [files, setFiles] = useState([]);
  const save = async () => {
    try {
      if (!form.amount) return toast.error("Amount required");
      const { data } = await api.post("/receipts", { ...form, amount: parseFloat(form.amount), date });
      toast.success("Receipt added");
      if (files.length) await uploadAttachments("txn", data.id, files);
      setFiles([]);
      setOpen(false);
      setForm({ amount: "", party_name: "", vehicle_number: "", description: "", mode: "cash", is_credit_recovery: false });
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-receipt-btn" className="h-10 px-4 rounded-md bg-card border border-input text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors">
          <Receipt size={16} /> Receipt
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Record Receipt</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className={label}>Party / source</label>
            <Autocomplete field="party" testid="receipt-party" value={form.party_name}
              onChange={(v) => setForm({ ...form, party_name: v })} placeholder="Party or 'Walk-in'" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Vehicle number</label>
              <Autocomplete field="vehicle" testid="receipt-vehicle" value={form.vehicle_number}
                onChange={(v) => setForm({ ...form, vehicle_number: v })} placeholder="optional" />
            </div>
            <div>
              <label className={label}>Amount (₹)</label>
              <input data-testid="receipt-amount" type="number" className={field} value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={label}>Description</label>
            <input data-testid="receipt-desc" className={field} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Note" />
          </div>
          <div>
            <label className={label}>Received in</label>
            <div className="grid grid-cols-2 gap-2">
              {["cash", "bank"].map((m) => (
                <button key={m} type="button" data-testid={`receipt-mode-${m}`} onClick={() => setForm({ ...form, mode: m })}
                  className={`h-10 rounded-md text-sm capitalize border flex items-center justify-center gap-1.5 transition-colors ${form.mode === m ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>
                  {m === "cash" ? <Wallet size={14} /> : <Landmark size={14} />} {m}
                </button>
              ))}
            </div>
          </div>
          <AttachPicker files={files} setFiles={setFiles} testid="receipt-attach" />
        </div>
        <DialogFooter>
          <button data-testid="receipt-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Add receipt</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Expense dialog ---------------- */
function ExpenseDialog({ onDone, date }) {
  const [open, setOpen] = useState(false);
  const blank = { amount: "", party_name: "", vehicle_number: "", description: "", category: "General", mode: "cash", employee_id: "" };
  const [form, setForm] = useState(blank);
  const [emps, setEmps] = useState(null);
  const [files, setFiles] = useState([]);
  const isSalary = form.category === "Salary";
  useEffect(() => {
    if (open && isSalary && emps === null) api.get("/employees").then((r) => setEmps((r.data || []).filter((e) => e.active !== false))).catch(() => setEmps([]));
  }, [open, isSalary, emps]);
  const save = async () => {
    try {
      if (!form.amount || !form.description) return toast.error("Amount and description required");
      if (isSalary && !form.employee_id) return toast.error("Select the employee for a salary expense");
      const { data } = await api.post("/expenses", { ...form, amount: parseFloat(form.amount), date });
      toast.success("Expense added");
      if (files.length) await uploadAttachments("txn", data.id, files);
      setFiles([]);
      setOpen(false);
      setForm(blank);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-expense-btn" className="h-10 px-4 rounded-md bg-card border border-input text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors">
          <Plus size={16} /> Expense
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Record Expense</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className={label}>Expense / paid for</label>
            <Autocomplete field="expense" testid="expense-desc" value={form.description}
              onChange={(v) => setForm({ ...form, description: v })} placeholder="Diesel, rent, salary…" />
          </div>
          <div>
            <label className={label}>Category</label>
            <select data-testid="expense-category" className={field} value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value, employee_id: "", party_name: e.target.value === "Salary" ? "" : form.party_name })}>
              {["General", "Fuel/Diesel", "Rent", "Salary", "Maintenance", "Office", "Transport", "Misc"].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {isSalary && (
            <div>
              <label className={label}>Employee <span className="text-destructive">*</span></label>
              <select data-testid="expense-employee" className={field} value={form.employee_id}
                onChange={(e) => { const emp = (emps || []).find((x) => x.id === e.target.value); setForm({ ...form, employee_id: e.target.value, party_name: emp ? emp.name : "" }); }}>
                <option value="">Select employee…</option>
                {(emps || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              {emps && emps.length === 0 && <p className="text-xs text-muted-foreground mt-1">No employees yet — add them on the Salary page first.</p>}
            </div>
          )}
          {isSalary && <p data-testid="expense-party-salary" className="text-xs text-muted-foreground -mt-1">Paid to: <span className="font-medium text-foreground">{form.party_name || "—"}</span></p>}
          <div className="grid grid-cols-2 gap-3">
            {!isSalary && (
            <div>
              <label className={label}>Party (optional)</label>
              <Autocomplete field="party" testid="expense-party" value={form.party_name}
                onChange={(v) => setForm({ ...form, party_name: v })} placeholder="optional" />
            </div>
            )}
            <div>
              <label className={label}>Vehicle (optional)</label>
              <Autocomplete field="vehicle" testid="expense-vehicle" value={form.vehicle_number}
                onChange={(v) => setForm({ ...form, vehicle_number: v })} placeholder="optional" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Amount (₹)</label>
              <input data-testid="expense-amount" type="number" className={field} value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div>
              <label className={label}>Paid from</label>
              <div className="grid grid-cols-2 gap-2">
                {["cash", "bank"].map((m) => (
                  <button key={m} type="button" data-testid={`expense-mode-${m}`} onClick={() => setForm({ ...form, mode: m })}
                    className={`h-10 rounded-md text-xs capitalize border transition-colors ${form.mode === m ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{m}</button>
                ))}
              </div>
            </div>
          </div>
          <AttachPicker files={files} setFiles={setFiles} testid="expense-attach" />
        </div>
        <DialogFooter>
          <button data-testid="expense-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Add expense</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ENTRY_LABELS = { invoice_payment: "Invoice payment", payment_receipt: "Receipt", receipt: "Receipt", voucher: "Payment voucher", income: "Income", bank_deposit: "Bank deposit", bank_deposit_in: "Bank deposit", salary: "Salary", salary_advance: "Salary advance", capital: "Capital" };

function BillAlert({ data, admin, onSaved }) {
  const limit = data?.bill_alert_amount || 0;
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState("");
  const missing = limit > 0 ? (data?.expenses || []).filter((e) => e.entry_type === "expense" && e.amount >= limit && !e.attachment_count) : [];
  const save = async () => {
    try { await api.put("/settings/bill-alert", { amount: parseFloat(v) || 0 }); toast.success("Missing bill alert saved"); setEdit(false); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!limit && !admin) return null;
  return (
    <div data-testid="bill-alert" className={`rounded-lg border px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm ${missing.length ? "border-destructive/40 bg-destructive/10" : "border-border bg-card"}`}>
      <FileWarning size={15} className={missing.length ? "text-destructive" : "text-muted-foreground"} />
      <span data-testid="bill-alert-text" className={missing.length ? "font-semibold text-destructive" : "text-muted-foreground"}>
        {!limit ? "Missing bill alert is off" : missing.length ? `${missing.length} expense(s) of ${inr(limit)} or more have no bill attached` : `All expenses of ${inr(limit)} or more have a bill`}
      </span>
      {admin && (edit ? (
        <span className="flex items-center gap-2">
          <input data-testid="bill-alert-input" type="number" min="0" className="h-8 w-28 px-2 rounded-md border border-input bg-card text-sm num" value={v} onChange={(e) => setV(e.target.value)} placeholder="0 = off" />
          <button data-testid="bill-alert-save" onClick={save} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors">Save</button>
          <button onClick={() => setEdit(false)} className="h-8 px-2 text-xs text-muted-foreground hover:underline">Cancel</button>
        </span>
      ) : <button data-testid="bill-alert-edit" onClick={() => { setV(limit || ""); setEdit(true); }} className="text-xs text-primary hover:underline">{limit ? "Change amount" : "Set amount"}</button>)}
    </div>
  );
}

function Row({ t, canWrite, onDelete, locked, onAttach, billLimit = 0 }) {
  const noBill = billLimit > 0 && t.entry_type === "expense" && t.amount >= billLimit && !t.attachment_count;
  const kind = t.entry_type === "expense" ? (t.category || "Expense") : (ENTRY_LABELS[t.entry_type] || (t.entry_type || "").replace(/_/g, " "));
  const title = t.party_name || t.description || "—";
  const details = [t.party_name ? t.description : "", t.vehicle_number, t.is_credit_recovery && "credit settled"].filter(Boolean).join(" · ");
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2 px-4 py-2.5 items-center group hover:bg-background/60 transition-colors">
      <div className="min-w-0 cursor-pointer" onClick={() => onAttach(t)} title="Open attachments">
        <div className="flex items-center gap-2 flex-wrap">
          {(t.invoice_no || t.voucher_no || t.receipt_no) && (
            <span className="num text-xs font-bold text-primary">{t.invoice_no || t.voucher_no || t.receipt_no}</span>
          )}
          <span className="text-sm font-medium break-words">{title}</span>
          {kind && <span data-testid={`txn-kind-${t.id}`} className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{kind}</span>}
          {t.account_name ? <span className="text-xs text-accent truncate">A/c: {t.account_name}</span> : null}
          <ModeBadge mode={t.pay_method || t.mode} />
          {noBill && <span data-testid={`no-bill-${t.id}`} className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-destructive text-destructive-foreground">No bill</span>}
        </div>
        <div data-testid={`txn-details-${t.id}`} className="text-xs text-muted-foreground break-words">
          {details || (t.party_name ? "No description" : "")}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="num text-sm font-semibold">{inr(t.amount)}</span>
        <AttachButton testid={`att-txn-${t.id}`} count={t.attachment_count} onClick={() => onAttach(t)} />
        {t.slip_path && (
          <button data-testid={`slip-${t.id}`} title="View deposit slip"
            onClick={async () => { try { const res = await api.get(`/files/${t.slip_path}`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data)); } catch { toast.error("Slip not available"); } }}
            className="text-xs text-primary hover:underline">Slip</button>
        )}
        {canWrite && !locked && (
          <button data-testid={`del-txn-${t.id}`} onClick={() => onDelete(t.id)}
            className="opacity-60 sm:opacity-0 sm:group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity">
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function CashToBankDialog({ onDone, date }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", deposit_time: "", deposited_by: "", bank_name: "", note: "" });
  const banks = useBankAccounts();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    try {
      if (!form.amount) return toast.error("Amount required");
      setBusy(true);
      let slip_path = "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        slip_path = up.data.path;
      }
      await api.post("/cash-deposit", { amount: parseFloat(form.amount), date, ...form, slip_path });
      toast.success("Cash deposited to bank");
      setOpen(false);
      setForm({ amount: "", deposit_time: "", deposited_by: "", bank_name: "", note: "" });
      setFile(null);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="cash-to-bank-btn" className="h-10 px-4 rounded-md bg-card border border-input text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors">
          <Plus size={16} /> Cash → Bank
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Deposit Cash to Bank</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Amount (₹)</label>
              <input data-testid="ctb-amount" type="number" className={field} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div><label className={label}>Time</label>
              <input data-testid="ctb-time" type="time" className={`${field} num`} value={form.deposit_time} onChange={(e) => setForm({ ...form, deposit_time: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Bank account</label>
              {banks.length > 0
                ? <BankSelect testid="ctb-bank" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} />
                : <Autocomplete field="bank" testid="ctb-bank" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} placeholder="e.g. HDFC Main Branch" />}</div>
            <div><label className={label}>Deposited by</label>
              <Autocomplete field="party" testid="ctb-by" value={form.deposited_by} onChange={(v) => setForm({ ...form, deposited_by: v })} placeholder="Person name" /></div>
          </div>
          <div><label className={label}>Note (optional)</label>
            <input data-testid="ctb-note" className={field} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          <div>
            <label className={label}>Deposit slip (image/PDF)</label>
            <input data-testid="ctb-slip" type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:h-9 file:px-3 file:rounded-md file:border file:border-input file:bg-muted file:text-sm file:cursor-pointer" />
            {file && <p className="text-xs text-muted-foreground mt-1 num">{file.name}</p>}
          </div>
          <p className="text-xs text-muted-foreground">Moves money from Cash to Bank — one entry out of cash, one into bank — on {date}.</p>
        </div>
        <DialogFooter>
          <button data-testid="ctb-save" disabled={busy} onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors disabled:opacity-60">{busy ? "Saving…" : "Deposit to bank"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CashBook() {
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const [date, setDate] = useState(today());
  const [lockedDates, setLockedDates] = useState([]);
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState(0);
  const [countOpen, setCountOpen] = useState(false);
  const [attFor, setAttFor] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/cashbook", { params: { date } });
    setData(data);
    const r = await api.get("/reminders");
    setReminders(r.data.length);
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/cashbook/locked-dates").then((r) => setLockedDates(r.data || [])).catch(() => setLockedDates([])); }, [data]);

  const del = async (id) => {
    const msg = isApprover(user)
      ? "Delete this entry? This cannot be undone."
      : "This entry will be sent to the admin for approval. Continue?";
    if (!window.confirm(msg)) return;
    const reason = askReason(user, "deleting", "this cash book entry");
    if (reason === null) return;
    const res = await api.delete(`/transactions/${id}`, { params: { reason } });
    if (res.data?.status === "pending") toast.info(res.data.message || "Sent for approval");
    else toast.success("Entry removed");
    load();
  };

  const b = data?.balances;
  const ds = data?.day_summary;
  const locked = !!data?.locked;
  const netToday = (ds?.receipts_total || 0) - (ds?.expenses_total || 0);

  const lockDay = () => setCountOpen(true);
  const unlockDay = async () => {
    try { await api.delete("/cashbook/lock", { params: { date } }); toast.success("Day unlocked"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const lockUntil = async () => {
    if (!window.confirm(`Finalise (lock) EVERY day up to and including ${date}? This locks all past cash book days at once. An accountant/admin can unlock individual days later.`)) return;
    try {
      const { data: r } = await api.post("/cashbook/lock-until", { date });
      toast.success(r.locked_count > 0 ? `Locked ${r.locked_count} day(s) up to ${date}` : "All those days were already locked");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout reminders={reminders}>
      <div className="p-8 max-w-6xl mx-auto">
        <PageHeader title="Cash Book" subtitle="Expenses on the left, receipts on the right — balanced daily.">
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-2">
              <input data-testid="cashbook-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="h-10 px-3 rounded-md border border-input bg-card text-sm num" />
              {lockedDates.some((x) => x.date === date) && (
                <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium"><Lock size={12} /> locked</span>
              )}
            </div>
            {lockedDates.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap justify-end max-w-[420px]" data-testid="locked-dates-list">
                <span className="text-[11px] text-muted-foreground">Finalised:</span>
                {lockedDates.slice(0, 8).map((l) => (
                  <button key={l.date} data-testid={`locked-chip-${l.date}`} onClick={() => setDate(l.date)}
                    title={`Locked by ${l.by || "—"}${l.at ? " on " + new Date(l.at).toLocaleString() : ""}`}
                    className={`num text-[10px] px-1.5 py-0.5 rounded border transition-colors ${l.date === date ? "bg-amber-500/20 border-amber-500/40 text-amber-700" : "bg-muted border-border text-muted-foreground hover:bg-amber-500/10"}`}>
                    <Lock size={9} className="inline mr-0.5 -mt-0.5" />{l.date}
                  </button>
                ))}
                {lockedDates.length > 8 && <span className="text-[11px] text-muted-foreground">+{lockedDates.length - 8} more</span>}
              </div>
            )}
          </div>
        </PageHeader>

        {b && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div data-testid="card-cash" className="rounded-lg border border-accent/20 bg-accent/5 px-4 py-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Wallet size={12} /> Cash in Hand</div>
              <div className="num text-xl font-bold text-accent">{inr(b.cash_balance)}</div>
              <div className="flex items-center gap-3 mt-1.5 text-xs num border-t border-accent/15 pt-1.5">
                <span className="text-[hsl(var(--positive))]" data-testid="cash-in-today">+{inr(ds?.receipts_cash || 0)}</span>
                <span className="text-destructive" data-testid="cash-out-today">−{inr(ds?.expenses_cash || 0)}</span>
                <span className="text-muted-foreground ml-auto">today</span>
              </div>
            </div>
            <div data-testid="card-bank" className="rounded-lg border border-primary/15 bg-primary/5 px-4 py-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Landmark size={12} /> Bank Balance</div>
              <div className="num text-xl font-bold text-primary">{inr(b.bank_balance)}</div>
              <div className="flex items-center gap-3 mt-1.5 text-xs num border-t border-primary/10 pt-1.5">
                <span className="text-[hsl(var(--positive))]" data-testid="bank-in-today">+{inr(ds?.receipts_bank || 0)}</span>
                <span className="text-destructive" data-testid="bank-out-today">−{inr(ds?.expenses_bank || 0)}</span>
                <span className="text-muted-foreground ml-auto">today</span>
              </div>
            </div>
            <div className="rounded-lg border border-border bg-card px-4 py-3">
              <div className="text-xs text-muted-foreground">Opening (Cash)</div>
              <div className="num text-lg font-semibold">{inr(b.opening_balance)}</div>
            </div>
            <div data-testid="card-net-today" className="rounded-lg border border-border bg-card px-4 py-3">
              <div className="text-xs text-muted-foreground">Net movement today</div>
              <div className={`num text-lg font-semibold ${netToday >= 0 ? "text-[hsl(var(--positive))]" : "text-destructive"}`}>{netToday >= 0 ? "+" : "−"}{inr(Math.abs(netToday))}</div>
              {ds?.credit_total ? <div className="text-xs text-muted-foreground num mt-0.5">Credit: {inr(ds.credit_total)}</div> : null}
            </div>
          </div>
        )}

        <PendingTransfers canWrite={canWrite} onDone={load} refreshKey={data} />

        {canWrite && (
          <div className="flex flex-wrap items-center gap-2 mb-5">
            {!locked && <>
              <InvoiceDialog onDone={load} date={date} />
              <ReceiptDialog onDone={load} date={date} />
              <ExpenseDialog onDone={load} date={date} />
              <CashToBankDialog onDone={load} date={date} />
              <BranchTransferDialog onDone={load} date={date} />
            </>}
            <div className="flex-1" />
            {locked ? (
              <div className="flex items-center gap-2">
                <span data-testid="cashbook-locked-badge" className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-600 bg-amber-500/10 border border-amber-500/30 px-3 h-10 rounded-md">
                  <Lock size={14} /> Finalised{data?.locked_by ? ` by ${data.locked_by}` : ""}
                </span>
                {(isApprover(user) || isAccountant(user)) && (
                  <button data-testid="cashbook-unlock-btn" onClick={unlockDay}
                    className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><LockOpen size={15} /> Unlock</button>
                )}
              </div>
            ) : (
              <>
                <button data-testid="cashbook-lock-until-btn" onClick={lockUntil}
                  className="h-10 px-4 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors"><Lock size={15} /> Lock all up to this date</button>
                <button data-testid="cashbook-lock-btn" onClick={lockDay}
                  className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"><Lock size={15} /> Final submit (lock day)</button>
              </>
            )}
          </div>
        )}

        <ClosingCountDialog open={countOpen} onOpenChange={setCountOpen} date={date} balances={b} onDone={load} />
        {locked && <ClosingCountSummary count={data?.closing_count} />}
        <BillAlert data={data} admin={isAdmin(user) || user?.platform_role === "superadmin"} onSaved={load} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* LEFT: expenses */}
          <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up">
            <div className="px-4 py-3 bg-destructive/5 border-b border-border flex items-start justify-between gap-2">
              <div>
                <span className="font-display text-lg font-semibold text-destructive">Payments / Expenses</span>
                <div className="text-xs text-muted-foreground mt-0.5 num flex items-center gap-1.5">
                  <Wallet size={11} /> Cash {inr(ds?.expenses_cash || 0)}
                  <span className="opacity-40">·</span>
                  <Landmark size={11} /> Bank {inr(ds?.expenses_bank || 0)}
                </div>
              </div>
              <span className="num text-sm font-bold text-destructive whitespace-nowrap">{inr(ds?.expenses_total ?? (data?.expenses || []).reduce((s, e) => s + e.amount, 0))}</span>
            </div>
            <div className="ledger-paper divide-y divide-border/70 min-h-[200px]">
              {data?.expenses?.length ? data.expenses.map((t) => (
                <Row key={t.id} t={t} canWrite={canWrite} onDelete={del} locked={locked} onAttach={setAttFor} billLimit={data?.bill_alert_amount || 0} />
              )) : <div className="p-8 text-center text-sm text-muted-foreground">No expenses on this day.</div>}
            </div>
          </div>

          {/* RIGHT: receipts */}
          <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up">
            <div className="px-4 py-3 bg-[hsl(var(--positive))]/10 border-b border-border flex items-start justify-between gap-2">
              <div>
                <span className="font-display text-lg font-semibold text-[hsl(var(--positive))]">Receipts (In)</span>
                <div className="text-xs text-muted-foreground mt-0.5 num flex items-center gap-1.5 flex-wrap">
                  <Wallet size={11} /> Cash {inr(ds?.receipts_cash || 0)}
                  <span className="opacity-40">·</span>
                  <Landmark size={11} /> Bank {inr(ds?.receipts_bank || 0)}
                  {ds?.credit_total ? <><span className="opacity-40">·</span> Credit {inr(ds.credit_total)}</> : null}
                </div>
              </div>
              <span className="num text-sm font-bold text-[hsl(var(--positive))] whitespace-nowrap">{inr(ds?.receipts_total ?? (data?.receipts || []).reduce((s, e) => s + e.amount, 0))}</span>
            </div>
            <div className="ledger-paper divide-y divide-border/70 min-h-[200px]">
              {data?.receipts?.length ? data.receipts.map((t) => (
                <Row key={t.id} t={t} canWrite={canWrite} onDelete={del} locked={locked} onAttach={setAttFor} />
              )) : <div className="p-8 text-center text-sm text-muted-foreground">No receipts on this day.</div>}
            </div>
          </div>
        </div>
        {canWrite && <CashbookLockReport refreshKey={lockedDates} />}
        <AttachmentsDialog open={!!attFor} onClose={() => setAttFor(null)} entityType="txn" entityId={attFor?.id}
          title={attFor ? `${attFor.voucher_no || attFor.receipt_no || attFor.invoice_no || attFor.party_name || attFor.description || ""} · ${inr(attFor.amount)}` : ""} onChanged={load} />
      </div>
    </Layout>
  );
}
